import { singleTextEllipsis } from '@byecode/ui'
import type { DTSelectItem, HighLightConditions, SelectField, StringArrayValue } from '@lighthouse/core'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { COLORS_MAP, LIGHT_COLORS_MAP } from '../../constants/color'
import { CellHighlighter } from '../Highlighter'
import { TooltipList, useWidth } from '../TooltipList'

interface SelectPreviewerProps extends Pick<React.CSSProperties, 'justifyContent'> {
    data?: StringArrayValue
    field: SelectField
    children?: React.ReactNode
    textStyles?: React.CSSProperties
    isWrap?: boolean
    width?: number
    colorless?: boolean
    styleConditions?: HighLightConditions
    direction?: 'horizontal' | 'vertical'
}

interface SelectItemPreviewerProps {
    children?: React.ReactNode
    id: string
    data: DTSelectItem
    colorless?: boolean
    isLast?: boolean
    isVertical?: boolean
    isWrap?: boolean
    direction?: 'horizontal' | 'vertical'
    textStyles?: React.CSSProperties
}

const SCxPersonListWrapper = styled.div<{ isWrap: boolean; isPadding?: boolean } & Pick<React.CSSProperties, 'justifyContent'>>`
    position: relative;
    max-width: 100%;
    height: 100%;
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: ${({ justifyContent }) => justifyContent};
    padding-right: ${({ isPadding }) => (isPadding ? '20px' : '0')};
    flex-wrap: ${({ isWrap }) => (isWrap ? 'wrap' : 'nowrap')};
    overflow: hidden;
`

const SCxCellValue = styled.span<{ direction?: 'horizontal' | 'vertical'; isLast?: boolean }>`
    padding: ${({ direction }) => (direction === 'vertical' ? '8px 2px' : '2px 8px')};
    border-radius: 100px;
    font-size: 14px;

    ${({ direction }) =>
        direction === 'vertical'
            ? css`
                  width: 22px;
              `
            : css`
                  height: 22px;
              `}
    line-height: 16px;
    white-space: nowrap;
    border: 1px solid var(--color-gray-200);
    ${({ isLast }) => isLast && singleTextEllipsis}
`

export const SelectItemPreviewer: React.FC<SelectItemPreviewerProps> = ({
    id,
    data,
    colorless = false,
    isLast = false,
    isWrap = false,
    direction,
    textStyles
}) => {
    const { label, color } = data
    const { ref: widthRef } = useWidth<HTMLDivElement>(id)
    const itemBasicStyle: React.CSSProperties = colorless
        ? { border: '1px solid var(--color-gray-200)', backgroundColor: 'var(--color-white)' }
        : {
              backgroundColor: color && `var(${LIGHT_COLORS_MAP[color]})`,
              color: color && `var(${COLORS_MAP[color]})`,
              borderColor: color && 'rgba(0,0,0,0.1)'
          }
    return (
        // <SCxTagContainer ref={widthRef}>
        // <TooltipText
        //     label={label}
        //     disabled={isWrap || disableTooltip}
        //     render={ref => {
        //         return (

        //         )
        //     }}
        // />
        <SCxCellValue key={label} ref={widthRef} direction={direction} isLast={isLast} style={{ ...itemBasicStyle, ...textStyles }}>
            {label}
        </SCxCellValue>
        // </SCxTagContainer>
    )
}

export const SelectValuePreviewer: React.FC<SelectPreviewerProps> = ({
    field,
    data = [],
    textStyles,
    width = 160,
    isWrap = true,
    justifyContent = 'flex-start',
    colorless = false,
    styleConditions,
    direction
}) => {
    const { id, select } = field
    const { options = [] } = select ?? {}

    const selectedOptions = useMemo(
        () =>
            data.reduce<DTSelectItem[]>((list, val) => {
                const option = options.find(option => option.label === val)
                list.push(option || { label: '找不到选项', id: 'notFound', color: 'error' })
                return list
            }, []),
        [data, options]
    )

    const selectText = data.join(',')

    return (
        <>
            <CellHighlighter fieldId={id} type="select" value={selectText} conditions={styleConditions} />
            <TooltipList
                data={selectedOptions.map(({ label }, index) => ({ label, value: String(index) }))}
                width={width}
                key={selectedOptions.map(item => item.label).join('-')}
                disabled={isWrap}
                render={visibleNum => {
                    const renderList = selectedOptions.slice(0, visibleNum)
                    return (
                        <SCxPersonListWrapper
                            justifyContent={justifyContent}
                            isPadding={visibleNum < selectedOptions.length}
                            isWrap={isWrap}
                        >
                            {renderList.map((item, index) => (
                                <SelectItemPreviewer
                                    key={item.id}
                                    id={String(index)}
                                    data={item}
                                    isLast={index === renderList.length - 1}
                                    textStyles={textStyles}
                                    colorless={colorless}
                                    isWrap={isWrap}
                                    direction={direction}
                                />
                            ))}
                        </SCxPersonListWrapper>
                    )
                }}
            />
        </>
    )
}
